import {
    NewBadge,
    PriceOption,
    VideoLength,
    LanguageCode,
    HotAndNewBadge,
    DifficultyLevel,
    SearchAggregation,
    CourseSearchFilters,
    CourseSearchSortType,
    CourseSubscriptionInfo,
    BrowseCourseSearchQuery,
    CourseSearchTriggerType,
    ClosedCaptionLanguageCode,
    BestSellerSubcategoryBadge,
    TopRatedCategoryTopicBadge,
    CourseSearchResponseMetadata,
    BestSellerCategoryTopicBadge,
    BestSellerSubcategoryTopicBadge,
    BrowseCourseSearchQueryVariables,
} from 'src/generated/graphql';

type BadgeType =
    | NewBadge
    | HotAndNewBadge
    | BestSellerSubcategoryBadge
    | TopRatedCategoryTopicBadge
    | BestSellerCategoryTopicBadge
    | BestSellerSubcategoryTopicBadge;

const NEW_BADGE_TYPE = 'NewBadge';
const HOT_AND_NEW_BADGE_TYPE = 'HotAndNewBadge';
const BESTSELLER_SUBCATEGORY_BADGE_TYPE = 'BestSellerSubcategoryBadge';
const TOP_RATED_CATEGORY_TOPIC_BADGE_TYPE = 'TopRatedCategoryTopicBadge';
const BESTSELLER_CATEGORY_TOPIC_BADGE_TYPE = 'BestSellerCategoryTopicBadge';
const BESTSELLER_SUBCATEGORY_TOPIC_BADGE_TYPE = 'BestSellerSubcategoryTopicBadge';

export const COURSE_BADGE_NEW = 'new';
export const COURSE_BADGE_TOP_RATED = 'top_rated';
export const COURSE_BADGE_BESTSELLER = 'bestseller';
export const COURSE_BADGE_HOT_AND_NEW = 'hot_and_new';

const TOP_RATED_BADGE_AVAILABLE_LANGUAGES = ['en', 'fr', 'pt', 'de', 'ja'];

const BADGE_FAMILY_MAP = {
    [NEW_BADGE_TYPE]: COURSE_BADGE_NEW,
    [HOT_AND_NEW_BADGE_TYPE]: COURSE_BADGE_HOT_AND_NEW,
    [BESTSELLER_SUBCATEGORY_BADGE_TYPE]: COURSE_BADGE_BESTSELLER,
    [TOP_RATED_CATEGORY_TOPIC_BADGE_TYPE]: COURSE_BADGE_TOP_RATED,
    [BESTSELLER_CATEGORY_TOPIC_BADGE_TYPE]: COURSE_BADGE_BESTSELLER,
    [BESTSELLER_SUBCATEGORY_TOPIC_BADGE_TYPE]: COURSE_BADGE_BESTSELLER,
};

export const normalizeUdemyUrl = (url: string | null | undefined): string | null => {
    if (!url || url.length === 0) {
        return null;
    }

    try {
        const urlObj = new URL(url);

        // Add trailing slash if it does not exist
        if (!urlObj.pathname.endsWith('/')) {
            urlObj.pathname += '/';
        }

        return `${urlObj.pathname}${urlObj.search}`;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Unable to normalize URL:', url, e);
        return url; // Return the original URL if normalization fails
    }
};

const filterAggregations = (aggregations: SearchAggregation[]) => {
    // Filtering aggregations to only show the ones that are in the control variant UI.
    return aggregations.filter((aggregation) => aggregation.key !== 'certification_topics');
};

export const mapSortOptions = (selectedSortOption: string | undefined) => {
    const getSelectedSortOption = (option: string | undefined) => {
        switch (option) {
            case 'highest-rated':
                return CourseSearchSortType.Rating;
            case 'newest':
                return CourseSearchSortType.Time;
            default:
                return CourseSearchSortType.Reviews;
        }
    };
    const CourseSortOptions = {
        NEWEST: {
            label: gettext('Newest'),
            key: 'newest',
            url: '',
        },
        RATING: {
            label: gettext('Highest Rated'),
            key: 'highest-rated',
            url: '',
        },
        MOST_REVIEWED: {
            label: gettext('Most Popular'),
            key: 'most-reviewed',
            url: '',
        },
    };
    const CourseSearchSortTypeToSortOption = new Map([
        [CourseSearchSortType.Time, CourseSortOptions.NEWEST],
        [CourseSearchSortType.Rating, CourseSortOptions.RATING],
        [CourseSearchSortType.Reviews, CourseSortOptions.MOST_REVIEWED],
    ]);
    const currentSortOption =
        CourseSearchSortTypeToSortOption.get(
            getSelectedSortOption(selectedSortOption) ?? CourseSearchSortType.Reviews,
        ) ?? CourseSortOptions.MOST_REVIEWED;
    return {
        current_sort_option: currentSortOption,
        options: [
            CourseSortOptions.RATING,
            CourseSortOptions.MOST_REVIEWED,
            CourseSortOptions.NEWEST,
        ].filter((option) => option.key !== currentSortOption.key),
    };
};

export const mapDurationFilterToGraphQLEnum = (durations: string[] = []): VideoLength[] => {
    const videoLengths: VideoLength[] = [];

    durations.forEach((duration) => {
        if (duration === 'extraShort') videoLengths.push(VideoLength.ExtraShort);
        if (duration === 'short') videoLengths.push(VideoLength.Short);
        if (duration === 'medium') videoLengths.push(VideoLength.Medium);
        if (duration === 'long') videoLengths.push(VideoLength.Long);
        if (duration === 'extraLong') videoLengths.push(VideoLength.ExtraLong);
    });

    return videoLengths;
};

export const mapFeaturesToGraphQLEnum = (features: string[] = []) => {
    const featuresMap: CourseSearchFilters = {};

    features.forEach((feature) => {
        if (feature === 'has_simple_quiz') featuresMap.mustHaveQuiz = true;
        if (feature === 'has_roleplay') featuresMap.mustHaveRoleplay = true;
        if (feature === 'has_practice_test') featuresMap.mustHavePracticeTest = true;
        if (feature === 'has_coding_exercises') featuresMap.mustHaveCodingExercise = true;
    });

    return featuresMap;
};

export const mapSortToGraphQLEnum = (sortOrder: string | null) => {
    switch (sortOrder) {
        case 'highest-rated':
            return CourseSearchSortType.Rating;
        case 'newest':
            return CourseSearchSortType.Time;
        default:
            return CourseSearchSortType.Reviews;
    }
};

export const mapPriceFilterToGraphQLEnum = (prices: string[] = []): PriceOption[] => {
    const priceOptions: PriceOption[] = [];

    prices.forEach((price) => {
        if (price === 'price-free') priceOptions.push(PriceOption.Free);
        if (price === 'price-paid') priceOptions.push(PriceOption.Paid);
    });

    return priceOptions;
};

export const mapInstructorLevelFromGraphQLEnum = (level: DifficultyLevel | null | undefined) => {
    switch (level) {
        case DifficultyLevel.Beginner:
            return gettext('Beginner');
        case DifficultyLevel.Intermediate:
            return gettext('Intermediate');
        case DifficultyLevel.Expert:
            return gettext('Expert');
        case DifficultyLevel.AllLevels:
            return gettext('All Levels');
        default:
            return '';
    }
};

export const mapLevelToGraphQLEnum = (levels: string[]) => {
    const difficultyLevels: DifficultyLevel[] = [];

    levels.forEach((level) => {
        switch (level) {
            case 'beginner':
                difficultyLevels.push(DifficultyLevel.Beginner);
                break;
            case 'intermediate':
                difficultyLevels.push(DifficultyLevel.Intermediate);
                break;
            case 'expert':
                difficultyLevels.push(DifficultyLevel.Expert);
                break;
            case 'all':
                difficultyLevels.push(DifficultyLevel.AllLevels);
                break;
        }
    });

    return difficultyLevels;
};

export const mapBadgeFromGraphQL = (badge: BadgeType) => {
    if (!badge) return null;

    return {
        badge_text: badge.name,
        id: BADGE_FAMILY_MAP[badge.__typename as keyof typeof BADGE_FAMILY_MAP],
        badge_family: BADGE_FAMILY_MAP[badge.__typename as keyof typeof BADGE_FAMILY_MAP],
    };
};

const getBadgeTypesToCheck = (language = '') => {
    const badgeTypesToCheck = [
        BESTSELLER_CATEGORY_TOPIC_BADGE_TYPE,
        BESTSELLER_SUBCATEGORY_BADGE_TYPE,
        BESTSELLER_SUBCATEGORY_TOPIC_BADGE_TYPE,
    ];

    if (TOP_RATED_BADGE_AVAILABLE_LANGUAGES.includes(language.split('-')[0])) {
        badgeTypesToCheck.push(TOP_RATED_CATEGORY_TOPIC_BADGE_TYPE);
    }

    badgeTypesToCheck.push(HOT_AND_NEW_BADGE_TYPE);
    badgeTypesToCheck.push(NEW_BADGE_TYPE);

    return badgeTypesToCheck;
};

export const isInUserSubscription = (subscriptionInfo?: CourseSubscriptionInfo) => {
    if (!subscriptionInfo) return false;

    return Boolean(
        subscriptionInfo.inSubscriptions?.find(
            (subscription) => subscription?.productType === 'CONSUMERSUBSCRIPTION',
        ),
    );
};

export const isInPersonalPlanCollection = (subscriptionInfo?: CourseSubscriptionInfo) => {
    if (!subscriptionInfo) return false;

    return Boolean(
        subscriptionInfo.availableInSubscriptions?.find(
            (subscription) => subscription?.productType === 'CONSUMERSUBSCRIPTION',
        ),
    );
};

const mapBadgesFromGraphQLResponse = (badges: BadgeType[], language: string) => {
    const badgeTypeToCheck = getBadgeTypesToCheck(language);

    const filteredBadges = badges.filter((badge) =>
        badgeTypeToCheck.includes(badge.__typename ?? ''),
    );

    const sortedBadges = [...filteredBadges].sort((a, b) => {
        const indexA = badgeTypeToCheck.indexOf(a.__typename ?? '');
        const indexB = badgeTypeToCheck.indexOf(b.__typename ?? '');
        return indexA - indexB;
    });

    const mappedBadge = mapBadgeFromGraphQL(sortedBadges[0]);

    return mappedBadge ? [mappedBadge] : [];
};

export const getContentInfo = (
    isPracticeTestCourse: boolean,
    practiceTestQuestionCount: number | undefined,
    durationInSeconds: number,
) => {
    if (isPracticeTestCourse && practiceTestQuestionCount) {
        return interpolate(
            ngettext('%(count)s question', '%(count)s questions', practiceTestQuestionCount),
            {count: practiceTestQuestionCount},
            true,
        );
    }

    const totalHours = Math.round((durationInSeconds / 3600) * 2) / 2;
    return totalHours
        ? interpolate(
              ngettext('%(hours)s total hour', '%(hours)s total hours', totalHours),
              {hours: totalHours},
              true,
          )
        : '';
};

export const mapSubsFilterTypeToGraphQLEnum = (type: string | null) => {
    switch (type) {
        case 'subs_only':
            return true;
        case 'purchasable_only':
            return false;
        default:
            return undefined;
    }
};

export const mapPageTypeToGraphQLEnum = (pageType: string) => {
    switch (pageType) {
        case 'category':
        case 'subs_category':
        case 'org_category':
            return CourseSearchTriggerType.BrowseCategory;
        case 'subcategory':
        case 'subs_subcategory':
        case 'org_subcategory':
            return CourseSearchTriggerType.BrowseSubcategory;
        case 'topic':
        case 'subs_topic':
        case 'org_topic':
            return CourseSearchTriggerType.BrowseTopic;
        default:
            return undefined;
    }
};

export const mapAggregationKeysFromGraphQLResponse = (key: string) => {
    switch (key) {
        case 'courseLabel':
            return 'course_label';
        default:
            return key;
    }
};

export const createFiltersFromSearchParams = (
    searchParams: URLSearchParams,
    pageId: number,
    selectedFilters?: CourseSearchFilters,
) => {
    let filters: CourseSearchFilters = {pageId: pageId.toString()};

    const ratings = searchParams.get('ratings');
    const subcategories = searchParams.getAll('subcategory');
    const courseLabels = searchParams.getAll('course_label');
    const certTopic = Boolean(searchParams.get('cert_topic'));
    const price = mapPriceFilterToGraphQLEnum(
        selectedFilters?.price || searchParams.getAll('price'),
    );
    const features = mapFeaturesToGraphQLEnum(searchParams.getAll('features'));
    const levels = mapLevelToGraphQLEnum(searchParams.getAll('instructional_level'));
    const videoLengths = mapDurationFilterToGraphQLEnum(searchParams.getAll('duration'));
    const subsFilterType = mapSubsFilterTypeToGraphQLEnum(searchParams.get('subs_filter_type'));
    const languages = searchParams
        .getAll('lang')
        .map((language) => language.toUpperCase() as LanguageCode);
    const closedCaptions = searchParams
        .getAll('closed_captions')
        .map((closedCaption) => closedCaption.toUpperCase() as ClosedCaptionLanguageCode);

    if (price.length > 0) filters.price = price;
    if (levels.length > 0) filters.level = levels;
    if (languages.length > 0) filters.language = languages;
    if (certTopic) filters.isCertificationPrep = certTopic;
    if (ratings) filters.minAverageRating = Number(ratings);
    if (courseLabels.length > 0) filters.topicIds = courseLabels;
    if (videoLengths.length > 0) filters.videoLength = videoLengths;
    if (subcategories.length > 0) filters.subcategoryIds = subcategories;
    if (subsFilterType !== undefined) filters.mustBeInConsumerSubscription = subsFilterType;
    if (closedCaptions.length > 0) filters.closedCaptionLanguage = closedCaptions;

    if (Object.keys(features).length > 0) filters = {...filters, ...features};

    return filters;
};

export const createVariablesFromSearchParams = (
    searchParams: URLSearchParams,
    pageId: number,
    pageType: string,
    selectedFilters?: CourseSearchFilters,
) => {
    const debug = Boolean(searchParams.get('debug'));
    const triggerType = mapPageTypeToGraphQLEnum(pageType);
    const sortOrder = mapSortToGraphQLEnum(searchParams.get('sort'));
    const page = Math.max((Number(searchParams.get('p')) || 1) - 1, 0);
    const filters = createFiltersFromSearchParams(searchParams, pageId, selectedFilters);

    if (triggerType === undefined) {
        throw new Error(`Invalid page type: ${pageType}`);
    }

    const variables: BrowseCourseSearchQueryVariables = {
        page,
        sortOrder,
        pageSize: 16,
        context: {triggerType, isDebug: debug || undefined},
    };

    if (Object.keys(filters).length > 0) variables.filters = filters;

    return variables;
};

export const createSearchDataFromGraphQLResponse = (
    sortOption: string | undefined,
    response?: BrowseCourseSearchQuery,
) => {
    if (!response?.courseSearch) return;

    const {results, count = 0, pageCount = 0, filterOptions, metadata} = response.courseSearch;
    const {esQuery} = metadata?.debug || ({} as CourseSearchResponseMetadata['debug']);

    return {
        count,
        query: esQuery,
        aggregations: filterAggregations(filterOptions)?.map(({key, label, buckets}) => ({
            title: label,
            allTitle: label,
            key: mapAggregationKeysFromGraphQLResponse(key),
            options: buckets.map((bucket) => ({
                title: bucket.label,
                value: bucket.value,
                selected: bucket.isSelected,
                count: bucket.countWithFilterApplied,
                key: mapAggregationKeysFromGraphQLResponse(bucket.key),
            })),
        })),
        sort_options: mapSortOptions(sortOption),
        pagination: {total_page: pageCount, total_item_count: count},
        items: results
            .filter((result) => !!result)
            .map(
                ({
                    searchDebugFeatures,
                    trackingId: courseTrackingId,
                    course: {
                        id,
                        title,
                        level,
                        isFree,
                        badges,
                        rating,
                        images,
                        locale,
                        headline,
                        updatedOn,
                        curriculum,
                        instructors,
                        urlAutoEnroll,
                        urlCourseLanding,
                        subscriptionInfo,
                        learningOutcomes,
                        durationInSeconds,
                        isPracticeTestCourse,
                    },
                }) => {
                    return {
                        title,
                        headline,
                        type: 'course',
                        id: Number(id),
                        is_paid: !isFree,
                        rating: rating?.average,
                        num_reviews: rating?.count,
                        last_update_date: updatedOn,
                        buyable_object_type: 'course',
                        tracking_id: courseTrackingId,
                        image_125_H: images?.height125,
                        image_240x135: images?.px240x135,
                        image_304x171: images?.px304x171,
                        image_480x270: images?.px480x270,
                        image_100x100: images?.px100x100,
                        input_features: searchDebugFeatures,
                        subscription_info: subscriptionInfo,
                        relevancy_score: searchDebugFeatures?.esScore,
                        predictive_score: searchDebugFeatures?.modelScore,
                        url: normalizeUdemyUrl(urlCourseLanding),
                        num_published_lectures: curriculum.contentCounts?.lecturesCount,
                        objectives_summary: learningOutcomes?.slice(0, 3).filter(Boolean),
                        badges: mapBadgesFromGraphQLResponse(badges as BadgeType[], locale),
                        instructional_level_simple: mapInstructorLevelFromGraphQLEnum(level),
                        free_course_subscribe_url: isFree ? normalizeUdemyUrl(urlAutoEnroll) : null,
                        is_in_user_subscription: isInUserSubscription(
                            subscriptionInfo as CourseSubscriptionInfo,
                        ),
                        is_in_personal_plan_collection: isInPersonalPlanCollection(
                            subscriptionInfo as CourseSubscriptionInfo,
                        ),
                        content_info: getContentInfo(
                            isPracticeTestCourse,
                            curriculum.contentCounts?.practiceTestQuestionsCount,
                            durationInSeconds,
                        ),
                        visible_instructors:
                            instructors?.map((instructor) => ({
                                id: parseInt(instructor.id, 10),
                                title: instructor.name,
                                display_name: instructor.name,
                            })) || [],
                    };
                },
            ),
    };
};
